/**
 * Ein Dialog, der auf dem Telefon von unten hereinfährt.
 *
 * Ein mittig schwebendes Kästchen ist eine Schreibtisch-Form: Es setzt voraus,
 * dass daneben Platz ist und dass eine Maus den Knopf in der Ecke trifft. Auf
 * einem Telefon gehört dasselbe Formular an den unteren Rand — dorthin, wo der
 * Daumen ist —, füllt die Breite und lässt sich mit einer Bewegung wegschieben,
 * statt einen 24 Pixel grossen „Abbrechen" zu verlangen.
 *
 * Umgebaut wird nicht der Dialog, sondern sein Verhalten: `alsBlatt()` nimmt
 * einen bestehenden `<dialog>` entgegen und hängt Griff und Zug daran. Die
 * Dialoge behalten damit ihren Aufbau, ihre Rückgabewerte und ihre
 * Schliess-Logik — die ist an drei Stellen mühsam gegen Plattformen abgesichert
 * worden und wird hier nicht angefasst.
 */

import { blattentscheid, zugweg } from '../geste.js';
import { istSchmal } from '../mobil.js';
import { symbol } from '../symbole.js';

/**
 * Wo darf der Zug beginnen?
 *
 * Am Griff immer. Im Inneren nur dort, wo nichts zu bedienen und nichts zu
 * scrollen ist — sonst zöge man beim Scrollen einer langen Feldliste das
 * ganze Blatt hinunter.
 */
function zugErlaubt(ereignis, blatt) {
  const griff = blatt.querySelector('.blattgriff');
  if (griff?.contains(ereignis.target)) return true;
  const kopf = blatt.querySelector('.blattkopf');
  return Boolean(kopf?.contains(ereignis.target));
}

/**
 * Aus einem Dialog ein Blatt machen.
 *
 * Auf breiten Schirmen bleibt alles, wie es war: Der Griff wird nicht
 * eingehängt, der Zug nicht verdrahtet. Die Entscheidung fällt beim Öffnen und
 * nicht bei jedem Zeigerereignis — wer das Fenster mitten im Dialog schmaler
 * zieht, hat andere Sorgen.
 */
export function alsBlatt(dialog) {
  dialog.classList.add('blatt');
  if (!istSchmal()) return dialog;

  // Nur der Griff, keine Überschrift: Jeder dieser Dialoge trägt seine eigene,
  // und zwei übereinander sagen dasselbe zweimal.
  const griff = document.createElement('div');
  griff.className = 'blattgriff';
  griff.innerHTML = '<span class="blattgriff-strich"></span>';
  dialog.prepend(griff);

  let startY = null;
  let startZeit = 0;
  let zieht = false;

  const beendeZug = () => {
    dialog.classList.remove('zieht');
    dialog.style.transform = '';
    startY = null;
    zieht = false;
  };

  dialog.addEventListener('pointerdown', (e) => {
    if (e.pointerType === 'mouse' || !zugErlaubt(e, dialog)) return;
    startY = e.clientY;
    startZeit = e.timeStamp;
    zieht = true;
    dialog.classList.add('zieht');
  });

  dialog.addEventListener('pointermove', (e) => {
    if (!zieht || startY === null) return;
    const dy = e.clientY - startY;
    dialog.style.transform = `translateY(${zugweg(dy)}px)`;
  });

  const loslassen = (e) => {
    if (!zieht || startY === null) return;
    const dy = e.clientY - startY;
    const schliessen = blattentscheid({
      dy, dauer: e.timeStamp - startZeit, hoehe: dialog.offsetHeight,
    });
    beendeZug();
    // `close()` statt einer eigenen Abbruchmeldung: Die Dialoge behandeln das
    // Ereignis bereits und geben dabei ihren Abbruchwert zurück.
    if (schliessen) dialog.close();
  };

  dialog.addEventListener('pointerup', loslassen);
  dialog.addEventListener('pointercancel', beendeZug);
  return dialog;
}

/**
 * Ein Blatt aus dem Nichts — für Inhalte, die keinen eigenen Dialog haben.
 *
 * Gibt zurück, was der Aufrufer über `schliesse(wert)` hineinreicht, sonst
 * `null`. Damit ist der Rückgabeweg derselbe wie bei den übrigen Dialogen.
 */
export function oeffneBlatt({ titel, klasse = '', aufbauen }) {
  return new Promise((fertig) => {
    const dialog = document.createElement('dialog');
    dialog.className = `blattdialog ${klasse}`.trim();

    const kopf = document.createElement('div');
    kopf.className = 'blattkopf';
    const ueberschrift = document.createElement('h2');
    ueberschrift.textContent = titel ?? '';
    const zu = document.createElement('button');
    zu.type = 'button';
    zu.className = 'blattschliessen';
    zu.setAttribute('aria-label', 'Schliessen');
    zu.append(symbol('schliessen', 20));
    kopf.append(ueberschrift, zu);

    const koerper = document.createElement('div');
    koerper.className = 'blattkoerper';

    let wert = null;
    let beendet = false;
    const schliesse = (ergebnis = null) => {
      wert = ergebnis;
      dialog.close();
    };

    dialog.append(kopf, koerper);
    koerper.append(aufbauen(schliesse));
    document.body.append(dialog);

    const beende = () => {
      if (beendet) return;
      beendet = true;
      dialog.remove();
      fertig(wert);
    };
    dialog.addEventListener('close', beende);
    zu.addEventListener('click', () => schliesse(null));
    // Ein Klick auf den Grund neben dem Blatt schliesst — auf dem Telefon der
    // gewohnte Weg, und auf dem Schreibtisch schadet er nicht.
    dialog.addEventListener('click', (e) => { if (e.target === dialog) schliesse(null); });

    alsBlatt(dialog);
    dialog.showModal();
  });
}
